import { Button } from '@/components/UI/Button';
import { ArrowLeft, Headset, Home } from 'lucide-react';
import { FC } from 'react';
import { FallbackProps, useErrorBoundary } from 'react-error-boundary';
import { useLocation, useNavigate } from 'react-router';

export const ErrorBoundaryFallback: FC<FallbackProps> = ({
  error
}: {
  error: {
    code: number;
  };
}) => {
  const { resetBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const location = useLocation();

  const handleGoBack = () => {
    resetBoundary();
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(-1); // Go back to the previous page in the browser history
    }
  };

  if (error?.code === 403) {
    return (
      <div className="grid h-full min-h-[calc(100vh-5.5rem)] place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8">
        <div className="text-center">
          <p className="text-primary text-base font-semibold">403</p>
          <h1 className="mt-4 text-5xl font-semibold tracking-tight text-balance text-gray-900 sm:text-2xl">
            Forbidden
          </h1>
          <p className="mt-4 max-w-3xl text-lg font-medium text-pretty text-gray-500 sm:text-lg/8">
            You do not have permissions to access this resource. Please contact your admin
            for more information.
          </p>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <Button
              size="lg"
              onClick={() => {
                resetBoundary();
                navigate('/');
              }}
              variant="outlined"
              color="secondary"
            >
              <Home />
              Go back home
            </Button>

            <Button
              size="lg"
              onClick={() => {
                handleGoBack();
              }}
            >
              <ArrowLeft />
              Back
            </Button>
            <Button
              size="lg"
              onClick={() => {
                resetBoundary();
                navigate('/contact-support');
              }}
            >
              <Headset />
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid h-full min-h-[calc(100vh-5.5rem)] place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8">
      <div className="text-center">
        <p className="text-primary text-base font-semibold">Error</p>
        <h1 className="mt-4 text-5xl font-semibold tracking-tight text-balance text-gray-900 sm:text-2xl">
          A client error occurred
        </h1>
        <p className="mt-4 max-w-3xl text-lg font-medium text-pretty text-gray-500 sm:text-lg/8">
          An error has occurred on the client side. Please try refreshing the page or
          contact support
        </p>
        <div className="mt-10 flex items-center justify-center gap-x-6">
          <Button
            size="lg"
            onClick={() => {
              resetBoundary();
              navigate(-1);
            }}
          >
            <ArrowLeft />
            Back
          </Button>
          <Button
            onClick={() => {
              resetBoundary();
              navigate('/');
            }}
            variant="outlined"
            color="secondary"
            size="lg"
          >
            <Home />
            Go back home
          </Button>
          <Button
            size="lg"
            onClick={() => {
              resetBoundary();
              navigate('/contact-support');
            }}
          >
            <Headset />
            Contact Support
          </Button>
        </div>
      </div>
    </div>
  );
};
